import { useContext } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { selectData } from 'layouts/onboarding'
import { useDashboardCalculations } from './useDashboardCalculations'

import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
} from 'date-fns'
import { LogoDev } from '@mui/icons-material'

const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function generateSSARows(baseAge, baseAmount) {
  const startAge = 62
  const endAge = 70
  const rows = []

  for (let age = startAge; age <= endAge; age++) {
    let income

    if (age === baseAge) {
      income = baseAmount
    } else if (age < baseAge) {
      // Go backwards (decrease by 5% per year)
      const yearsDiff = baseAge - age
      income = baseAmount * Math.pow(0.95, yearsDiff)
    } else {
      // Go forward (increase by 7% per year)
      const yearsDiff = age - baseAge
      income = baseAmount * Math.pow(1.07, yearsDiff)
    }

    rows.push({
      id: age,
      age: `Age ${age}`,
      income: `${dollarCentsFormat.format(income || 0)}`,
    })
  }

  return rows
}

function getFRA(year) {
  if (year < 1938) return 65
  if (year > 1960) return 67

  const table = {
    1938: 65.2,
    1939: 65.3,
    1940: 65.5,
    1941: 65.7,
    1942: 65.8,
    1943: 66.0,
    1944: 66.0,
    1945: 66.0,
    1946: 66.0,
    1947: 66.0,
    1948: 66.0,
    1949: 66.0,
    1950: 66.0,
    1951: 66.0,
    1952: 66.0,
    1953: 66.0,
    1954: 66.0,
    1955: 66.2,
    1956: 66.3,
    1957: 66.5,
    1958: 66.7,
    1959: 66.8,
    1960: 67.0,
  }

  return table[year] || 67
}

// function calculateSSBenefit({
//   fraAge, // e.g., 67
//   baseIncome, // user input amount
//   baseType, // "amountAt62" or "fullRetirementAge"
//   claimAgeYears, // e.g., 65
//   claimAgeMonths, // e.g., 6 (optional)
//   cola = 0.026,
// }) {
//   const claimMonths = claimAgeYears * 12 + (claimAgeMonths || 0)
//   const fraMonths = fraAge * 12
//   const age62Months = 62 * 12

//   let fraIncome

//   if (baseType === 'fullRetirementAge') {
//     fraIncome = baseIncome
//   } else if (baseType === 'amountAt62') {
//     // Reverse reduction to find FRA income
//     const monthsEarly = fraMonths - age62Months
//     let factor = 1

//     // First 36 months early → 0.556% per month reduction
//     const earlyFirst36 = Math.min(monthsEarly, 36)
//     factor /= 1 - earlyFirst36 * (5 / 9 / 100)

//     // Beyond 36 months early → 0.417% per month reduction
//     const earlyBeyond36 = Math.max(monthsEarly - 36, 0)
//     factor /= 1 - earlyBeyond36 * (5 / 12 / 100)

//     fraIncome = baseIncome * factor
//   }

//   // Now adjust from FRA to claim age
//   const monthsDiff = claimMonths - fraMonths
//   let claimIncome = fraIncome

//   if (monthsDiff < 0) {
//     // Claiming early
//     const monthsEarly = Math.abs(monthsDiff)

//     const earlyFirst36 = Math.min(monthsEarly, 36)
//     claimIncome *= 1 - earlyFirst36 * (5 / 9 / 100)

//     const earlyBeyond36 = Math.max(monthsEarly - 36, 0)
//     claimIncome *= 1 - earlyBeyond36 * (5 / 12 / 100)
//   } else if (monthsDiff > 0) {
//     // Claiming late
//     claimIncome *= 1 + monthsDiff * (2 / 3 / 100)
//   }

//   return claimIncome
// }

export function calculateSSBenefit({
  fraAge, // e.g., 67
  baseIncome, // numeric value from intake
  baseType, // 'fullRetirementAge', 'current', 'amountAt62'
  claimAgeYears, // integer years
  claimAgeMonths, // integer months
  birthYear,
  currentYear = new Date().getFullYear(),
  colaRate = 0.026, // 2.6% annual COLA
}) {
  const fraMonths = fraAge * 12
  const claimMonths = claimAgeYears * 12 + claimAgeMonths
  const age62Months = 62 * 12

  let fraIncome = baseIncome

  // 1️⃣ Determine FRA income depending on baseType
  if (baseType === 'fullRetirementAge') {
    // Already at FRA value, nothing to change
    fraIncome = baseIncome
  } else if (baseType === 'current') {
    // Grow current income to FRA date - need to work on this one
    const yearsToFRA = fraAge - (currentYear - (birthYear + claimAgeYears))
    fraIncome = baseIncome * Math.pow(1 + colaRate, yearsToFRA)
  } else if (baseType === 'amountAt62') {
    // Reverse reduction to get FRA income
    const monthsEarly = fraMonths - age62Months

    const earlyFirst36 = Math.min(monthsEarly, 36) * (5 / 9 / 100)
    const earlyBeyond36 = Math.max(monthsEarly - 36, 0) * (5 / 12 / 100)

    const totalReduction = earlyFirst36 + earlyBeyond36

    fraIncome = baseIncome / (1 - totalReduction)
  }

  // 2️⃣ Adjust FRA income based on claiming early or late
  let monthlyBenefit = fraIncome
  const monthDiff = claimMonths - fraMonths

  if (monthDiff < 0) {
    // Early claiming penalty
    const earlyFirst36 = Math.min(-monthDiff, 36) * (5 / 9 / 100)
    const earlyBeyond36 = Math.max(-monthDiff - 36, 0) * (5 / 12 / 100)
    const totalReduction = earlyFirst36 + earlyBeyond36
    monthlyBenefit = fraIncome * (1 - totalReduction)
  } else if (monthDiff > 0) {
    // Delayed retirement credits (2/3 of 1% per month after FRA)
    const delayedCredits = monthDiff * (2 / 3 / 100)
    monthlyBenefit = fraIncome * (1 + delayedCredits)
  }

  // 3️⃣ Apply COLA after claim date for projection years
  // const claimYear = birthYear + claimAgeYears
  // const yearsAfterClaim = projectionYear - claimYear
  // console.log('yearsAfterClaim', yearsAfterClaim)
  // if (yearsAfterClaim > 0) {
  //   monthlyBenefit *= Math.pow(1 + colaRate, yearsAfterClaim)
  // }

  return monthlyBenefit
}

// console.log(
//   'calcBenefit',
//   calculateSSBenefit({
//     fraAge: 67,
//     baseIncome: 2390,
//     baseType: 'fullRetirementAge',
//     claimAgeYears: 62,
//     claimAgeMonths: 0,
//     projectionYear: 2032,
//     birthYear: 1970,
//   })
// )

export const useSocialSecurityCalculations = () => {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // const { intakeState } = useContext(IntakeContext)

    // const dob = new Date(intakeState.dateOfBirth)
    // const scd = new Date(intakeState.dateOfHire)

    return {
      generateSSARows,
      calculateSSBenefit,
      getFRA,
    }
  } catch (e) {
    return {}
  }
}
